import { TableScroll } from '@web/components/table-scroll.tsx';
import { QuoteAreaPricingCard } from './area-pricing-card.tsx';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { readerFor } from '../../i18n/localized.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { temporaryId } from '../../hooks/quote-merge.ts';
import { calculateQuote, formatCents, onSiteMinutes } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import type { QuoteCalculationResult } from '@shared/calculations/pricing.ts';
import {
  BILLING_INCREMENTS,
  MAX_CREW_SIZE,
  SERIES_FREQUENCIES,
  type BillingBasis,
  type DiscountType,
  type SeriesFrequency,
} from '@shared/constants/index.ts';
import { adoptStandingDiscountOnSoleTier, seedFrequencyTiers, standingTierDiscount } from '@shared/frequency-tiers.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Quote, QuoteFee, QuoteFrequencyTier } from '@shared/types/index.ts';

/**
 * Step 5 (spec §16.1): rate, per-room time adjustments, fees, discount, tax and
 * the optional manual total override.
 */
interface PricingStepProps {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  preview: QuoteCalculationResult;
  currencyCode: string;
  readOnly: boolean;
  canOverrideTotal: boolean;
  /**
   * What this editing session knows about a tier's discount that the quote
   * cannot say for itself: `cleared` for one the estimator emptied, `adopted`
   * for one the step filled in from the company's standing discount as a
   * second frequency joined it (see `setTierOffered`). Either note is dropped
   * the moment the estimator sets that frequency's discount themselves.
   *
   * Owned by the editor rather than by this step, which unmounts every time
   * the estimator visits another one.
   */
  tierDiscountNotes: Map<SeriesFrequency, TierDiscountNote>;
}

/**
 * An `adopted` note carries the discount it put there, and is spent only on a
 * tier still holding exactly that. The draft can be replaced under it by the
 * server's own version — a save conflict reloaded, an action in the review
 * step — and a note describing a discount that is no longer on the quote then
 * says nothing about the one that is.
 */
export type TierDiscountNote =
  { kind: 'cleared' } | { kind: 'adopted'; discountType: DiscountType | null; discountValue: number };

export default function PricingStep({
  quote,
  update,
  preview,
  currencyCode,
  readOnly,
  canOverrideTotal,
  tierDiscountNotes,
}: PricingStepProps) {
  const { t } = useTranslation();
  const { company } = useSession();
  /* The quote's own language; see `roomLabel` in rooms-step.tsx. */
  const localized = readerFor(quote.language);
  const [overrideOpen, setOverrideOpen] = useState(quote.totals.totalOverrideCents !== null);
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());

  /*
    Each offered tier's live price, through the same shared engine the totals
    panel already runs — so the figure beside the weekly row is exactly what
    the Worker will snapshot onto the tier when this draft saves.
  */
  // `??` throughout: a draft recovered from IndexedDB can predate migration
  // 0110 and carry neither tier field.
  const quoteTiers = quote.frequencyTiers ?? [];

  const tierPreviews = useMemo(() => {
    const previews = new Map<SeriesFrequency, { totalCents: number; frequencyDiscountCents: number }>();
    if (quoteTiers.length === 0) return previews;
    const input = calculationInputFor(quote);
    for (const tier of quoteTiers) {
      const result = calculateQuote({
        ...input,
        frequencyTierDiscount: { discountType: tier.discountType, discountValue: tier.discountValue },
      });
      previews.set(tier.frequency, {
        totalCents: result.totalCents,
        frequencyDiscountCents: result.frequencyDiscountCents,
      });
    }
    return previews;
  }, [quote]);

  function patchTotals(patch: Partial<Quote['totals']>) {
    update((draft) => ({ ...draft, totals: { ...draft.totals, ...patch } }));
  }

  function patchRoom(roomId: string, patch: { adjustedMinutes: number | null }) {
    update((draft) => ({
      ...draft,
      rooms: draft.rooms.map((room) => (room.id === roomId ? { ...room, ...patch } : room)),
    }));
  }

  function mutateFees(mutate: (fees: QuoteFee[]) => QuoteFee[]) {
    update((draft) => ({ ...draft, fees: mutate(draft.fees).map((fee, index) => ({ ...fee, displayOrder: index })) }));
  }

  const discountType = quote.totals.discountType;
  const isCommercial = quote.segment === 'commercial';
  const hasOverride = quote.totals.totalOverrideCents !== null;

  const tierRank = (frequency: SeriesFrequency) => SERIES_FREQUENCIES.indexOf(frequency);

  function setTierOffered(frequency: SeriesFrequency, offered: boolean) {
    update((draft) => {
      const offeredTiers = draft.frequencyTiers ?? [];
      if (!offered) {
        const remaining = offeredTiers.filter((tier) => tier.frequency !== frequency);
        tierDiscountNotes.delete(frequency);
        // Taking the added frequency back off makes this a one-frequency quote
        // again, so the tier that adopted a discount to be one of a choice goes
        // back to the price as built. Nothing the estimator typed is undone —
        // typing it drops the note.
        const alone = remaining.length === 1 ? remaining[0] : undefined;
        const note = alone ? tierDiscountNotes.get(alone.frequency) : undefined;
        const adopted =
          note?.kind === 'adopted' &&
          note.discountType === alone?.discountType &&
          note.discountValue === alone.discountValue;
        if (!alone || !adopted) {
          return { ...draft, frequencyTiers: remaining };
        }
        tierDiscountNotes.delete(alone.frequency);
        return {
          ...draft,
          frequencyTiers: remaining.map((tier) =>
            tier.frequency === alone.frequency ? { ...tier, discountType: null, discountValue: 0 } : tier,
          ),
        };
      }
      if (offeredTiers.some((tier) => tier.frequency === frequency)) return draft;
      // A re-offered frequency starts from the company's standing discount
      // for it, which is also what a fresh quote is seeded with. A quote that
      // held one frequency was quoting the price as built; now that it offers
      // a choice, that tier takes its standing discount too — otherwise the
      // frequency the quote was generated for is the one option missing its
      // discount, which is exactly backwards.
      const standing = company ? standingTierDiscount(company.frequencyTierSettings, frequency) : null;
      const added: QuoteFrequencyTier = {
        frequency,
        discountType: standing?.discountType ?? null,
        discountValue: standing?.discountValue ?? 0,
        frequencyDiscountCents: 0,
        totalCents: 0,
      };
      // Except when the estimator is the one who set that lone tier to no
      // discount: a tier holding nothing because a quote was generated for one
      // frequency and one holding nothing because someone chose "No discount"
      // look identical on the quote, so the choice is remembered here for as
      // long as it is being made.
      const sole = offeredTiers.length === 1 ? offeredTiers[0] : undefined;
      const kept =
        company && !(sole && tierDiscountNotes.get(sole.frequency)?.kind === 'cleared')
          ? adoptStandingDiscountOnSoleTier(offeredTiers, company.frequencyTierSettings)
          : offeredTiers;
      const adoptedTier = sole && kept[0] !== sole ? kept[0] : undefined;
      if (sole && adoptedTier) {
        tierDiscountNotes.set(sole.frequency, {
          kind: 'adopted',
          discountType: adoptedTier.discountType,
          discountValue: adoptedTier.discountValue,
        });
      }
      return {
        ...draft,
        frequencyTiers: [...kept, added].sort((a, b) => tierRank(a.frequency) - tierRank(b.frequency)),
      };
    });
  }

  /** Every call is the estimator's own hand on that frequency's discount. */
  function patchTier(frequency: SeriesFrequency, patch: Partial<QuoteFrequencyTier>) {
    tierDiscountNotes.delete(frequency);
    update((draft) => ({
      ...draft,
      frequencyTiers: (draft.frequencyTiers ?? []).map((tier) =>
        tier.frequency === frequency ? { ...tier, ...patch } : tier,
      ),
    }));
  }

  function toggleFrequencyTiers(enabled: boolean) {
    update((draft) => {
      // Switching on for the first time seeds the company's offered tiers;
      // switching off keeps the rows, so the discounts survive for next time.
      const shouldSeed = enabled && (draft.frequencyTiers ?? []).length === 0 && company;
      return {
        ...draft,
        frequencyTiersEnabled: enabled,
        frequencyTiers: shouldSeed
          ? seedFrequencyTiers(company.frequencyTierSettings).map((tier) => ({
              ...tier,
              frequencyDiscountCents: 0,
              totalCents: 0,
            }))
          : (draft.frequencyTiers ?? []),
      };
    });
  }

  return (
    <div className="space-y-6">
      <QuoteAreaPricingCard quote={quote} update={update} readOnly={readOnly} />
      {/* ---- Billing basis (commercial only) ----------------------------- */}
      {/*
        What the total buys: one visit, or a month of the specified service.
        Only a commercial quote may price by the month — residential quotes
        never see this card, and stay pixel-identical to before it existed.
      */}
      {isCommercial ? (
        <section className="card" aria-labelledby="billing-basis-heading">
          <h2 id="billing-basis-heading" className="mb-3 text-lg font-semibold">
            {t('pricingStep.billingHeading')}
          </h2>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Select
              label={t('pricingStep.billingBasisLabel')}
              hint={t('pricingStep.billingBasisHint')}
              value={quote.billingBasis}
              disabled={readOnly}
              onChange={(event) => {
                const basis = event.target.value as BillingBasis;
                update((draft) => ({
                  ...draft,
                  billingBasis: basis,
                  // The engine reads a null visit count as one a week; making
                  // that visible in the select beats leaving a blank the
                  // price silently interprets.
                  visitsPerWeek: basis === 'monthly' ? (draft.visitsPerWeek ?? 1) : draft.visitsPerWeek,
                }));
              }}
              options={[
                { value: 'per_visit', label: t('pricingStep.billingBasisPerVisit') },
                { value: 'monthly', label: t('pricingStep.billingBasisMonthly') },
              ]}
            />
            {quote.billingBasis === 'monthly' ? (
              <Select
                label={t('pricingStep.visitsPerWeekLabel')}
                hint={t('pricingStep.visitsPerWeekHint')}
                value={String(quote.visitsPerWeek ?? 1)}
                disabled={readOnly}
                onChange={(event) => update((draft) => ({ ...draft, visitsPerWeek: Number(event.target.value) }))}
                options={[1, 2, 3, 4, 5, 6, 7].map((times) => ({
                  value: String(times),
                  label: t('pricingStep.visitsPerWeekOption', { times }),
                }))}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ---- Rate and rounding ------------------------------------------ */}
      <section className="card" aria-labelledby="rate-heading">
        <h2 id="rate-heading" className="mb-3 text-lg font-semibold">
          {t('pricingStep.rateHeading')}
        </h2>
        <div className="grid gap-x-4 sm:grid-cols-2">
          {quote.areaPricing?.mode !== 'area' && (
            <>
              <MoneyInput
                currencyCode={currencyCode}
                label={t('pricingStep.hourlyRate')}
                hint={t('pricingStep.hourlyRateHint')}
                valueCents={quote.totals.hourlyRateCents}
                disabled={readOnly}
                min={0}
                onChangeCents={(cents) => patchTotals({ hourlyRateCents: cents ?? 0 })}
              />
              <Select
                label={t('pricingStep.roundUpTo')}
                hint={t('pricingStep.roundUpToHint')}
                value={String(quote.totals.billingIncrementMinutes)}
                disabled={readOnly}
                onChange={(event) => patchTotals({ billingIncrementMinutes: Number(event.target.value) })}
                options={BILLING_INCREMENTS.map((increment) => ({
                  value: String(increment.minutes),
                  label: t(`schedule.billingIncrement.${increment.minutes}`),
                }))}
              />
            </>
          )}
          <NumberInput
            label={t('pricingStep.crewSize')}
            hint={t('pricingStep.crewSizeHint')}
            value={quote.crewSize}
            blankValue={1}
            min={1}
            max={MAX_CREW_SIZE}
            step={1}
            disabled={readOnly}
            onValueChange={(value) =>
              update((draft) => ({
                ...draft,
                crewSize: Math.min(MAX_CREW_SIZE, Math.max(1, Math.trunc(value ?? 1) || 1)),
              }))
            }
          />
        </div>
      </section>

      {/* ---- Per-room time ---------------------------------------------- */}
      <section className="card" aria-labelledby="time-heading">
        <h2 id="time-heading" className="mb-1 text-lg font-semibold">
          {t('pricingStep.timeHeading')}
        </h2>
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('pricingStep.timeDescription')}</p>

        <TableScroll aria-label={t('pricingStep.timeTableLabel')} className="overflow-x-auto">
          <table className="data-table w-full min-w-max border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-line)]">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('pricingStep.columnRoom')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('pricingStep.columnCalculated')}
                </th>
                <th scope="col" className="py-2 font-semibold">
                  {t('pricingStep.columnUseInstead')}
                </th>
              </tr>
            </thead>
            <tbody>
              {quote.rooms.map((room, index) => {
                const calculated = preview.rooms[index];
                return (
                  <tr key={room.id} className="border-b border-[color:var(--color-line)] last:border-0">
                    {/* A room name runs to 120 characters. Bounded, so the
                        name wraps in its own column instead of pushing the
                        override field off a phone. */}
                    <td className="table-text max-w-64 py-2 pr-4">
                      {localized.text(room.roomName, room.roomNameTranslations)}
                      {room.quantity > 1 ? ` ×${room.quantity}` : ''}
                      {room.adjustedMinutes !== null ? (
                        <span className="ml-2 badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                          {t('pricingStep.adjusted')}
                        </span>
                      ) : null}
                    </td>
                    <td className="tabular py-2 pr-4 text-right">
                      {formatMinutes(calculated?.calculatedMinutes ?? 0)}
                    </td>
                    <td className="py-2">
                      <label className="sr-only" htmlFor={`adjust-${room.id}`}>
                        {t('pricingStep.adjustedMinutesLabel', {
                          room: localized.text(room.roomName, room.roomNameTranslations),
                        })}
                      </label>
                      <input
                        id={`adjust-${room.id}`}
                        type="number"
                        min={0}
                        max={100000}
                        className="field-control max-w-32"
                        placeholder="—"
                        disabled={readOnly}
                        value={room.adjustedMinutes ?? ''}
                        onChange={(event) =>
                          patchRoom(room.id, {
                            adjustedMinutes:
                              event.target.value.trim() === '' ? null : Math.max(0, Number(event.target.value) || 0),
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>

        {/*
          Three separate facts once a room can carry a price: how long the crew
          is on site, how much of that is billed by the hour, and what the
          priced rooms add. Collapsing them into "Total 1 hr, billed as 0 min →
          $0.00" described an all-priced quote as free.
        */}
        <p className="mt-4 text-sm" aria-live="polite">
          {quote.crewSize > 1
            ? t('pricingStep.totalTimeOnSiteCrew', {
                // The ROUTINE visit's labor, not the estimate: on a monthly
                // quote the estimate holds the whole month, and dividing that
                // would report half a month as time on site. Per-visit quotes
                // read the same either way — the two figures are equal there —
                // and this is the figure the totals panel, the public quote
                // and the booking handoff all divide, so the surfaces agree.
                onsite: formatMinutes(onSiteMinutes(preview.routineVisitMinutes, quote.crewSize)),
                crew: quote.crewSize,
                estimated: formatMinutes(preview.estimatedMinutes),
              })
            : t('pricingStep.totalTimeOnSite', { estimated: formatMinutes(preview.estimatedMinutes) })}
          {!preview.pricingBlocked &&
          quote.areaPricing?.mode !== 'area' &&
          preview.billableMinutes !== preview.estimatedMinutes
            ? t('pricingStep.totalHourly', { billable: formatMinutes(preview.billableMinutes) })
            : ''}
          {preview.pricingBlocked
            ? t('areaPricing.unpriced')
            : t('pricingStep.totalArrow', { amount: money(preview.laborSubtotalCents) })}
          {preview.rooms.some((room) => room.chargeCents !== null)
            ? t('pricingStep.totalRooms', { amount: money(preview.roomChargesCents) })
            : ''}
        </p>
      </section>

      {/* ---- Fees -------------------------------------------------------- */}
      <section className="card" aria-labelledby="fees-heading">
        <h2 id="fees-heading" className="mb-3 text-lg font-semibold">
          {t('pricingStep.feesHeading')}
        </h2>

        {quote.fees.length === 0 ? (
          <p className="mb-4 text-[color:var(--color-muted)]">{t('pricingStep.noFees')}</p>
        ) : (
          <ul className="mb-4 space-y-3">
            {quote.fees.map((fee) => (
              <li
                key={fee.id}
                className="flex flex-wrap items-end gap-3 border-b border-[color:var(--color-line)] pb-3"
              >
                <div className="min-w-48 flex-1">
                  <TextInput
                    label={t('pricingStep.description')}
                    value={fee.name}
                    disabled={readOnly}
                    onChange={(event) =>
                      mutateFees((fees) =>
                        fees.map((item) => (item.id === fee.id ? { ...item, name: event.target.value } : item)),
                      )
                    }
                  />
                </div>
                <div className="w-40">
                  <MoneyInput
                    currencyCode={currencyCode}
                    label={t('pricingStep.amount')}
                    valueCents={fee.amountCents}
                    disabled={readOnly}
                    onChangeCents={(cents) =>
                      mutateFees((fees) =>
                        fees.map((item) => (item.id === fee.id ? { ...item, amountCents: cents ?? 0 } : item)),
                      )
                    }
                  />
                </div>
                <div className="mb-4 flex items-center gap-3">
                  <Checkbox
                    label={t('pricingStep.taxable')}
                    checked={fee.isTaxable}
                    disabled={readOnly}
                    onChange={(event) =>
                      mutateFees((fees) =>
                        fees.map((item) => (item.id === fee.id ? { ...item, isTaxable: event.target.checked } : item)),
                      )
                    }
                  />
                  {!readOnly ? (
                    <Button
                      variant="ghost"
                      onClick={() => mutateFees((fees) => fees.filter((item) => item.id !== fee.id))}
                      aria-label={t('pricingStep.removeFee', { name: fee.name })}
                    >
                      {t('common.remove')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!readOnly ? (
          <Button
            onClick={() =>
              mutateFees((fees) => [
                ...fees,
                {
                  id: temporaryId(),
                  name: t('pricingStep.defaultFeeName'),
                  description: null,
                  amountCents: 0,
                  isTaxable: true,
                  displayOrder: fees.length,
                },
              ])
            }
          >
            {t('pricingStep.addFee')}
          </Button>
        ) : null}
      </section>

      {/* ---- Discount and tax -------------------------------------------- */}
      <section className="card" aria-labelledby="adjust-heading">
        <h2 id="adjust-heading" className="mb-3 text-lg font-semibold">
          {t('pricingStep.discountTaxHeading')}
        </h2>

        <div className="grid gap-x-4 sm:grid-cols-2">
          <Select
            label={t('pricingStep.discountLabel')}
            value={discountType ?? ''}
            disabled={readOnly}
            onChange={(event) => {
              const value = event.target.value;
              patchTotals({
                discountType: value === '' ? null : (value as 'fixed' | 'percentage'),
                discountValue: 0,
              });
            }}
            options={[
              { value: '', label: t('pricingStep.noDiscount') },
              { value: 'fixed', label: t('pricingStep.fixedAmountOff') },
              { value: 'percentage', label: t('pricingStep.percentageOff') },
            ]}
          />

          {discountType === 'fixed' ? (
            <MoneyInput
              currencyCode={currencyCode}
              label={t('pricingStep.amountOff')}
              valueCents={quote.totals.discountValue}
              disabled={readOnly}
              min={0}
              onChangeCents={(cents) => patchTotals({ discountValue: cents ?? 0 })}
            />
          ) : null}

          {discountType === 'percentage' ? (
            <NumberInput
              label={t('pricingStep.percentOff')}
              min={0}
              max={100}
              step={0.5}
              disabled={readOnly}
              // Stored as basis points; shown as a percentage (spec §9.9).
              // No discount shows as an empty box: a 0 sitting in the field is
              // something to delete before typing, and it cannot be deleted.
              blankValue={0}
              value={quote.totals.discountValue / 100}
              onValueChange={(percent) =>
                patchTotals({
                  discountValue: Math.max(0, Math.min(10_000, Math.round((percent ?? 0) * 100))),
                })
              }
            />
          ) : null}
        </div>

        <div className="grid gap-x-4 sm:grid-cols-2">
          <NumberInput
            label={t('pricingStep.taxRate')}
            min={0}
            max={100}
            step={0.001}
            disabled={readOnly}
            hint={t('pricingStep.taxRateHint')}
            // Same as the discount above: "we do not charge tax" is an empty
            // box, which is also what the hint underneath it describes.
            blankValue={0}
            value={quote.totals.taxRateBasisPoints / 100}
            onValueChange={(percent) =>
              patchTotals({
                taxRateBasisPoints: Math.max(0, Math.round((percent ?? 0) * 100)),
              })
            }
          />
          <div className="pt-7">
            <Checkbox
              label={t('pricingStep.laborTaxable')}
              hint={t('pricingStep.laborTaxableHint')}
              checked={quote.totals.laborIsTaxable}
              disabled={readOnly}
              onChange={(event) => patchTotals({ laborIsTaxable: event.target.checked })}
            />
          </div>
        </div>
      </section>

      {/* ---- Frequency options (per-visit quotes only) -------------------- */}
      {/*
        The same scope offered at more than one service frequency, each at its
        own per-visit price, with the customer choosing when they accept
        (migration 0110). A monthly quote never sees this card: it already
        prices its frequencies inside the month.
      */}
      {quote.billingBasis === 'per_visit' ? (
        <section className="card" aria-labelledby="frequency-tiers-heading">
          <h2 id="frequency-tiers-heading" className="mb-1 text-lg font-semibold">
            {t('pricingStep.frequencyTiersHeading')}
          </h2>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('pricingStep.frequencyTiersDescription')}</p>

          <Checkbox
            label={t('pricingStep.frequencyTiersEnable')}
            hint={t('pricingStep.frequencyTiersEnableHint')}
            checked={quote.frequencyTiersEnabled}
            disabled={readOnly || (hasOverride && !quote.frequencyTiersEnabled)}
            onChange={(event) => toggleFrequencyTiers(event.target.checked)}
          />
          {hasOverride ? (
            <div className="mt-3">
              <Alert tone="info">{t('pricingStep.frequencyTiersOverrideConflict')}</Alert>
            </div>
          ) : null}

          {quote.frequencyTiersEnabled ? (
            <TableScroll aria-label={t('pricingStep.frequencyTiersTableLabel')} className="mt-4 overflow-x-auto">
              <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-[color:var(--color-line)]">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      {t('pricingStep.tierColumnFrequency')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      {t('pricingStep.tierColumnDiscount')}
                    </th>
                    <th scope="col" className="py-2 text-right font-semibold">
                      {t('pricingStep.tierColumnPrice')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SERIES_FREQUENCIES.map((frequency) => {
                    const tier = quoteTiers.find((candidate) => candidate.frequency === frequency);
                    const livePreview = tier ? tierPreviews.get(frequency) : undefined;
                    return (
                      <tr key={frequency} className="border-b border-[color:var(--color-line)] last:border-0">
                        <td className="py-2 pr-4 align-top">
                          <Checkbox
                            label={t(`schedule.frequency.${frequency}`)}
                            checked={Boolean(tier)}
                            disabled={readOnly}
                            onChange={(event) => setTierOffered(frequency, event.target.checked)}
                          />
                        </td>
                        <td className="py-2 pr-4">
                          {tier ? (
                            <div className="flex flex-wrap items-start gap-2">
                              <div className="w-40">
                                <Select
                                  label={t('pricingStep.discountLabel')}
                                  value={tier.discountType ?? ''}
                                  disabled={readOnly}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    patchTier(frequency, {
                                      discountType: value === '' ? null : (value as DiscountType),
                                      discountValue: 0,
                                    });
                                    // Remembered so that offering another
                                    // frequency later does not read this
                                    // emptied tier as a quote that was only
                                    // ever built for one.
                                    if (value === '') tierDiscountNotes.set(frequency, { kind: 'cleared' });
                                  }}
                                  options={[
                                    { value: '', label: t('pricingStep.noDiscount') },
                                    { value: 'percentage', label: t('pricingStep.percentageOff') },
                                    { value: 'fixed', label: t('pricingStep.fixedAmountOff') },
                                  ]}
                                />
                              </div>
                              {tier.discountType === 'percentage' ? (
                                <div className="w-28">
                                  <NumberInput
                                    label={t('pricingStep.percentOff')}
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    disabled={readOnly}
                                    blankValue={0}
                                    value={tier.discountValue / 100}
                                    onValueChange={(percent) =>
                                      patchTier(frequency, {
                                        discountValue: Math.max(0, Math.min(10_000, Math.round((percent ?? 0) * 100))),
                                      })
                                    }
                                  />
                                </div>
                              ) : null}
                              {tier.discountType === 'fixed' ? (
                                <div className="w-36">
                                  <MoneyInput
                                    currencyCode={currencyCode}
                                    label={t('pricingStep.amountOff')}
                                    valueCents={tier.discountValue}
                                    disabled={readOnly}
                                    min={0}
                                    onChangeCents={(cents) => patchTier(frequency, { discountValue: cents ?? 0 })}
                                  />
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-[color:var(--color-muted)]">{t('pricingStep.tierNotOffered')}</span>
                          )}
                        </td>
                        <td className="tabular py-2 text-right align-top">
                          {tier && livePreview ? (
                            <>
                              <div>
                                {preview.pricingBlocked ? t('areaPricing.unpriced') : money(livePreview.totalCents)}
                              </div>
                              {livePreview.frequencyDiscountCents > 0 ? (
                                <div className="text-xs text-[color:var(--color-muted)]">
                                  {t('pricingStep.tierSaves', { amount: money(livePreview.frequencyDiscountCents) })}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('pricingStep.frequencyTiersFootnote')}</p>
            </TableScroll>
          ) : null}
        </section>
      ) : null}

      {/* ---- Manual override --------------------------------------------- */}
      <section className="card" aria-labelledby="override-heading">
        <h2 id="override-heading" className="mb-3 text-lg font-semibold">
          {t('pricingStep.finalPriceHeading')}
        </h2>

        <p className="mb-4 text-lg">
          <Trans
            i18nKey="pricingStep.calculatedTotal"
            values={{ amount: preview.pricingBlocked ? t('areaPricing.unpriced') : money(preview.totalCents) }}
            components={{ strong: <strong className="tabular" /> }}
          />
        </p>

        {!canOverrideTotal ? (
          <Alert tone="info">{t('pricingStep.noOverridePermission')}</Alert>
        ) : quote.frequencyTiersEnabled && !overrideOpen ? (
          // The mutual exclusion, said where the button would have been: an
          // override replaces the calculated total, and the frequency options
          // ARE calculated totals — see the same rule server-side.
          <Alert tone="info">{t('pricingStep.overrideTiersConflict')}</Alert>
        ) : overrideOpen ? (
          <>
            <MoneyInput
              currencyCode={currencyCode}
              label={t('pricingStep.chargeInstead')}
              valueCents={quote.totals.totalOverrideCents}
              disabled={readOnly}
              min={0}
              onChangeCents={(cents) => patchTotals({ totalOverrideCents: cents })}
            />
            <TextInput
              label={t('pricingStep.whyLabel')}
              hint={t('pricingStep.whyHint')}
              value={quote.totals.totalOverrideReason ?? ''}
              disabled={readOnly}
              onChange={(event) => patchTotals({ totalOverrideReason: event.target.value || null })}
            />
            <Button
              onClick={() => {
                patchTotals({ totalOverrideCents: null, totalOverrideReason: null });
                setOverrideOpen(false);
              }}
              disabled={readOnly}
            >
              {t('pricingStep.useCalculatedTotal')}
            </Button>
          </>
        ) : (
          <Button onClick={() => setOverrideOpen(true)} disabled={readOnly}>
            {t('pricingStep.setDifferentPrice')}
          </Button>
        )}
      </section>

      {/* ---- Agreement terms (commercial only) --------------------------- */}
      {/*
        The contract wrapped around a commercial quote, rendered under
        "Agreement terms" on the customer's page and copied to the schedule
        at booking. Null fields mean what a missing clause means on paper,
        which is why every input here treats blank as null rather than zero.
      */}
      {isCommercial ? (
        <section className="card" aria-labelledby="agreement-heading">
          <h2 id="agreement-heading" className="mb-1 text-lg font-semibold">
            {t('pricingStep.agreementHeading')}
          </h2>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('pricingStep.agreementDescription')}</p>
          <div className="grid gap-x-4 sm:grid-cols-3">
            <NumberInput
              label={t('pricingStep.termMonthsLabel')}
              hint={t('pricingStep.termMonthsHint')}
              min={1}
              max={120}
              value={quote.contract.termMonths}
              disabled={readOnly}
              onValueChange={(months) =>
                update((draft) => ({ ...draft, contract: { ...draft.contract, termMonths: months ?? null } }))
              }
            />
            <NumberInput
              label={t('pricingStep.escalationLabel')}
              hint={t('pricingStep.escalationHint')}
              min={0}
              max={100}
              step={0.5}
              // Stored as basis points, shown as a percentage — the same
              // translation the discount above does.
              value={quote.contract.escalationBasisPoints === null ? null : quote.contract.escalationBasisPoints / 100}
              disabled={readOnly}
              onValueChange={(percent) =>
                update((draft) => ({
                  ...draft,
                  contract: {
                    ...draft.contract,
                    escalationBasisPoints:
                      percent === null ? null : Math.max(0, Math.min(10_000, Math.round(percent * 100))),
                  },
                }))
              }
            />
            <NumberInput
              label={t('pricingStep.noticeDaysLabel')}
              hint={t('pricingStep.noticeDaysHint')}
              min={0}
              max={365}
              value={quote.contract.cancellationNoticeDays}
              disabled={readOnly}
              onValueChange={(days) =>
                update((draft) => ({
                  ...draft,
                  contract: { ...draft.contract, cancellationNoticeDays: days ?? null },
                }))
              }
            />
          </div>
          <Checkbox
            label={t('pricingStep.autoRenewsLabel')}
            hint={t('pricingStep.autoRenewsHint')}
            checked={quote.contract.autoRenews}
            disabled={readOnly}
            onChange={(event) =>
              update((draft) => ({ ...draft, contract: { ...draft.contract, autoRenews: event.target.checked } }))
            }
          />
        </section>
      ) : null}
    </div>
  );
}
